import { useState } from "react";
import { useTranslation } from "react-i18next";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

const supportEmail = "hello@profioplatform.com";

export default function Contact() {
  const { t } = useTranslation();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const contactDetails = [
    { label: t("contact.email"), value: supportEmail },
    { label: t("contact.hours"), value: t("contact.hoursValue") },
  ];

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setStatus("success");
        setForm({ name: "", email: "", message: "" });
      } else {
        const data = await res.json().catch(() => ({}));
        const code = data?.error || "ERROR";
        const msgs = {
          INVALID_NAME: t("contact.form.errorName", { defaultValue: "Please enter your name." }),
          INVALID_EMAIL: t("contact.form.errorEmail", { defaultValue: "Please enter a valid email." }),
          INVALID_MESSAGE: t("contact.form.errorMessage", { defaultValue: "Message must be at least 10 characters." }),
        };
        setErrorMsg(msgs[code] || t("contact.form.errorGeneric", { defaultValue: "Something went wrong. Please try again." }));
        setStatus("error");
      }
    } catch {
      setErrorMsg(t("contact.form.errorGeneric", { defaultValue: "Something went wrong. Please try again." }));
      setStatus("error");
    }
  }

  return (
    <main className="relative">
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-20 bg-brand-slate" aria-hidden="true" />
        <div
          className="absolute inset-x-0 top-0 -z-10 h-[420px]"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(520px 320px at 10% 0%, rgba(152,222,163,0.24), transparent 72%), radial-gradient(480px 280px at 95% 15%, rgba(114,105,227,0.22), transparent 72%)",
          }}
        />

        <div className="mx-auto max-w-6xl px-4 md:px-6 pt-16 pb-28 md:pt-20 md:pb-20">
          <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] items-start">
            <div className="space-y-7">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-mint/10 px-4 py-1 text-sm font-medium text-brand-mint">
                {t("contact.badge")}
              </span>
              <h1 className="font-unbounded text-4xl md:text-5xl font-semibold leading-tight">
                {t("contact.title")}
              </h1>
              <p className="text-lg text-white/80 leading-relaxed">
                {t("contact.subtitle")}
              </p>

              <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
                {contactDetails.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-[0_16px_40px_rgba(39,44,57,0.45)] backdrop-blur"
                  >
                    <dt className="text-sm font-medium uppercase tracking-wide text-white/60">
                      {item.label}
                    </dt>
                    <dd className="mt-2 text-lg font-semibold text-white">
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <form
              onSubmit={handleSubmit}
              noValidate
              className="relative overflow-hidden rounded-3xl border border-brand-violet/40 bg-brand-violet/10 p-6 md:p-8 shadow-[0_20px_60px_rgba(39,44,57,0.55)]"
            >
              <div
                className="pointer-events-none absolute -top-24 right-[-80px] h-60 w-60 rounded-full bg-brand-pink/30 blur-3xl"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute -bottom-16 left-[-60px] h-52 w-52 rounded-full bg-brand-mint/20 blur-3xl" aria-hidden="true" />

              <div className="relative space-y-6">
                {status === "success" ? (
                  <div className="flex flex-col items-center gap-4 py-10 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#98dea3]/15 ring-1 ring-[#98dea3]/40">
                      <svg viewBox="0 0 24 24" className="h-7 w-7 text-[#98dea3]" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </div>
                    <h2 className="font-unbounded text-xl font-semibold">
                      {t("contact.form.successTitle", { defaultValue: "Message sent!" })}
                    </h2>
                    <p className="text-sm text-white/65">
                      {t("contact.form.successBody", { defaultValue: "We'll get back to you as soon as possible." })}
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-2 text-sm text-white/60 underline underline-offset-2 hover:text-white transition"
                    >
                      {t("contact.form.sendAnother", { defaultValue: "Send another message" })}
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <h2 className="font-unbounded text-2xl font-semibold">
                        {t("contact.form.title")}
                      </h2>
                      <p className="mt-2 text-white/75 text-sm">
                        {t("contact.form.subtitle")}
                      </p>
                    </div>

                    <label className="block text-sm font-medium text-white/80" htmlFor="contact-name">
                      {t("contact.form.name")}
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        autoComplete="name"
                        required
                        className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-brand-mint focus:outline-none focus:ring-2 focus:ring-brand-mint/40"
                        placeholder={t("contact.form.namePlaceholder")}
                      />
                    </label>

                    <label className="block text-sm font-medium text-white/80" htmlFor="contact-email">
                      {t("contact.form.email")}
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                        className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-brand-mint focus:outline-none focus:ring-2 focus:ring-brand-mint/40"
                        placeholder={t("contact.form.emailPlaceholder")}
                      />
                    </label>

                    <label className="block text-sm font-medium text-white/80" htmlFor="contact-message">
                      {t("contact.form.message")}
                      <textarea
                        id="contact-message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={5}
                        required
                        className="mt-2 w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-brand-mint focus:outline-none focus:ring-2 focus:ring-brand-mint/40"
                        placeholder={t("contact.form.messagePlaceholder")}
                      />
                    </label>

                    {status === "error" && errorMsg && (
                      <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-400 ring-1 ring-red-500/20">
                        {errorMsg}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-violet px-5 py-3 text-base font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-mint/60 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "sending" ? (
                        <>
                          <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          {t("contact.form.sending", { defaultValue: "Sending…" })}
                        </>
                      ) : (
                        t("contact.form.submit")
                      )}
                    </button>

                    <p className="text-xs text-white/60">
                      {t("contact.form.privacy")}
                    </p>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
